import InventoryQuantityIcon from '@/components/Icons/InventoryQuantity';
import ProductTagIcon from '@/components/Icons/ProductTag';
import { ItemConditionEnum, LocationConditionEnum } from '@/constants/enum';
import { PATH } from '@/constants/path';
import useUserPlan from '@/hooks/useUserPlan';
import ruleSlice from '@/redux/slice/rule.slice';
import { IItemCondition, ILocationCondition } from '@/types/newSetup.type';
import { InlineStack } from '@shopify/polaris';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BoldText from '../BoldText';
import ButtonIconVertical from '../ButtonIconVertical';
import CustomCard from '../customCard';
import { CollectionPlusIcon, CountryIcon, ZipcodeIcon } from '../Icons';
import RegularText from '../RegularText';
import { RecommendedRulesStyled } from './styled';

interface IRecommendedRulesProps {
  isInCard?: boolean;
  reachedRuleLimit: boolean;
}

interface IRuleItem {
  icon: React.ReactElement;
  title: string;
  type?: 'itemConditions' | 'locationConditions';
  condition?: IItemCondition['type'] | ILocationCondition['type'];
  disabled?: boolean;
  tooltip?: React.ReactNode;
}

const RecommendedRules = ({ isInCard, reachedRuleLimit }: IRecommendedRulesProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isFreePlan, isPlanLimited } = useUserPlan();

  const handleConditionClick = useCallback(
    (item: IRuleItem) => {
      navigate(PATH.CREATE_RULE, { state: { isDefaultSelectDisplayConditionTab: true } });
      dispatch(ruleSlice.actions.handleInit());
      if (item.type && item.condition) {
        dispatch(
          ruleSlice.actions.handleChange({
            key: item.type,
            value: [{ type: item.condition, value: [{ data: '' }] }] as IItemCondition[] | ILocationCondition[],
          }),
        );

        let defaultOpen = {
          product: false,
          collection: false,
          vendor: false,
          exception: false,
          inventoryLocations: false,
          productTags: false,
          skus: false,
          country: false,
        };
        if (item.type === 'itemConditions') {
          switch (item.condition) {
            case ItemConditionEnum.IncludeProduct:
            case ItemConditionEnum.ExcludeProduct:
              defaultOpen.product = true;
              break;
            case ItemConditionEnum.IncludeCollection:
            case ItemConditionEnum.ExcludeCollection:
              defaultOpen.collection = true;
              break;
            case ItemConditionEnum.IncludeVendor:
            case ItemConditionEnum.ExcludeVendor:
              defaultOpen.vendor = true;
              break;
            // case ItemConditionEnum.IncludeInventoryLocation:
            // case ItemConditionEnum.ExcludeInventoryLocation:
            //   defaultOpen.inventoryLocations = true;
            //   break;
            case ItemConditionEnum.IncludeSKU:
            case ItemConditionEnum.ExcludeSKU:
              defaultOpen.skus = true;
              break;
            case ItemConditionEnum.IncludeProductTag:
            case ItemConditionEnum.ExcludeProductTag:
              defaultOpen.productTags = true;
              break;
            case ItemConditionEnum.InventoryQuantity: {
              dispatch(ruleSlice.actions.handleSetError({ value: { field: 'inventoryQuantity', msg: 'Error' } }));
              break;
            }
            default:
              break;
          }
          dispatch(ruleSlice.actions.handleSetIsDefaultOpenModalAddCondition(defaultOpen));
        } else if (item.type === 'locationConditions') {
          switch (item.condition) {
            case LocationConditionEnum.Country:
              setTimeout(() => {
                dispatch(
                  ruleSlice.actions.handleSetError({
                    value: { field: 'country', msg: 'Please select at least 1 country' },
                  }),
                );
              }, 0);
              break;
            case LocationConditionEnum.Zipcode:
              setTimeout(() => {
                dispatch(
                  ruleSlice.actions.handleSetError({
                    value: { field: 'zipcode', msg: 'Please enter at least 1 zipcode' },
                  }),
                );
              }, 0);
              break;
          }
        }
      }
    },
    [dispatch, navigate],
  );

  const listRuleByCondition = useMemo<IRuleItem[]>(() => {
    const isDisabledItemCondition = isFreePlan;
    const isDisabledLocationCondition = isFreePlan || isPlanLimited;
    return [
      // {
      //   icon: <BoxIcon />,
      //   title: 'All products',
      // },
      // {
      //   icon: <BoxPlusIcon />,
      //   title: 'Specific products',
      //   type: 'itemConditions',
      //   condition: ItemConditionEnum.IncludeProduct,
      //   disabled: isDisabledItemCondition,
      //   tooltip: isDisabledItemCondition ? 'Available on Standard plan or above' : undefined,
      // },
      {
        icon: <CollectionPlusIcon />,
        title: 'Specific collections',
        type: 'itemConditions',
        condition: ItemConditionEnum.IncludeCollection,
        disabled: isDisabledItemCondition,
        tooltip: isDisabledItemCondition ? 'Available on Standard plan or above' : undefined,
      },
      {
        icon: <ProductTagIcon />,
        title: 'Product tags',
        type: 'itemConditions',
        condition: ItemConditionEnum.IncludeProductTag,
        disabled: isDisabledLocationCondition,
        tooltip: isDisabledLocationCondition ? 'Available on Pro plan' : undefined,
      },
      // {
      //   icon: <InventoryLocationIcon />,
      //   title: 'Inventory locations',
      //   type: 'itemConditions',
      //   condition: ItemConditionEnum.IncludeInventoryLocation,
      //   disabled: isDisabledLocationCondition,
      //   tooltip: isDisabledLocationCondition ? 'Available on Pro plan' : undefined,
      // },
      {
        icon: <InventoryQuantityIcon />,
        title: 'Inventory quantity',
        type: 'itemConditions',
        condition: ItemConditionEnum.InventoryQuantity,
        disabled: isDisabledLocationCondition,
        tooltip: isDisabledLocationCondition ? 'Available on Pro plan' : undefined,
      },
      {
        icon: <CountryIcon />,
        title: 'Regions, Countries',
        type: 'locationConditions',
        condition: LocationConditionEnum.Country,
        disabled: isDisabledLocationCondition,
        tooltip: isDisabledLocationCondition ? 'Available on Pro plan' : undefined,
      },
      {
        icon: <ZipcodeIcon />,
        title: 'Zipcodes',
        type: 'locationConditions',
        condition: LocationConditionEnum.Zipcode,
        disabled: isDisabledLocationCondition,
        tooltip: isDisabledLocationCondition ? 'Available on Pro plan' : undefined,
      },
    ];
  }, [isFreePlan, isPlanLimited]);

  const content = useMemo(
    () => (
      <RecommendedRulesStyled>
        <BoldText>Recommended rules</BoldText>
        <br />
        <RegularText>Create estimated delivery date customized by specific conditions</RegularText>
        <div className="mt-8">
          <InlineStack gap="200" wrap>
            {listRuleByCondition.map((item) => (
              <div key={item.title} className="condition-item">
                <ButtonIconVertical
                  icon={item.icon}
                  onClick={() => handleConditionClick(item)}
                  tooltip={item.tooltip || (reachedRuleLimit ? 'Limit reached' : undefined)}
                  disabled={item.disabled || reachedRuleLimit}
                  disabledBackgroundColor={item.disabled ? '#FFB8000D' : undefined}
                  fullHeight
                >
                  {item.title}
                </ButtonIconVertical>
              </div>
            ))}
          </InlineStack>
        </div>
      </RecommendedRulesStyled>
    ),
    [handleConditionClick, listRuleByCondition, reachedRuleLimit],
  );

  return isInCard ? content : <CustomCard>{content}</CustomCard>;
};

export default memo(RecommendedRules);
